import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import {
  downloadWriteOffVoucher,
  printWriteOffVoucher,
  useOrders,
  useRequestWriteOff,
  useWriteOffSuggestion,
} from '@/api/hooks'
import type { CreditNote } from '@/api/types'
import { ErrorBanner, formatMoney } from '@/components/common'

const TYPES = [
  { code: 'WRITE_OFF', label: 'Write-off', hint: 'A balance nobody is going to collect.' },
  { code: 'DISCOUNT', label: 'Discount', hint: 'A reduction agreed with the customer after billing.' },
]

/**
 * Letting part of an order's balance go.
 *
 * <p>The shortfall is worked out and offered rather than typed: billed, less
 * paid, less anything already let go. More than that is refused by the server —
 * a note for money that was never owed is not a write-off, it is a hole in the
 * ledger.</p>
 *
 * <p>Nothing moves until somebody with the authority agrees. Writing off what a
 * customer owes is a decision, and one taken quietly by whoever happens to be
 * on the screen is how a business stops noticing that its bills are not being
 * paid.</p>
 */
export function WriteOffDialog({
  open,
  fixedOrderId,
  onClose,
}: {
  open: boolean
  /** Set when raised from an order, where there is nothing to choose. */
  fixedOrderId?: string
  onClose: () => void
}) {
  const [pickedOrder, setPickedOrder] = useState('')
  const orderId = fixedOrderId ?? pickedOrder
  // Only needed when there is an order to choose; opened from one, there is not.
  const { data: orders } = useOrders({ page: 0, size: 100 }, !fixedOrderId)
  const { data: shortfall } = useWriteOffSuggestion(open && orderId ? orderId : undefined)
  const request = useRequestWriteOff()
  const [noteType, setNoteType] = useState('WRITE_OFF')
  const [amount, setAmount] = useState('')
  const [tax, setTax] = useState('')
  const [reason, setReason] = useState('')
  const [raised, setRaised] = useState<CreditNote | null>(null)

  const chosen = TYPES.find((type) => type.code === noteType)
  // No tax comes back on a bad debt: the sale stands and the tax on it was due.
  const taxable = noteType !== 'WRITE_OFF'

  const close = () => {
    setPickedOrder('')
    setAmount('')
    setTax('')
    setReason('')
    setRaised(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{raised ? `Raised ${raised.creditNoteNumber}` : 'Write off a balance'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={request.error} />

        {raised ? (
          <Alert severity={raised.status === 'ISSUED' ? 'success' : 'info'} sx={{ mt: 1 }}>
            {formatMoney(raised.totalAmount)} against {raised.orderNumber}.{' '}
            {raised.status === 'ISSUED'
              ? 'Approved — the balance has come down by this amount.'
              : 'It is waiting for approval; the balance still stands until then.'}
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {fixedOrderId ? null : (
              <TextField
                select
                fullWidth
                size="small"
                label="Order"
                value={pickedOrder}
                onChange={(event) => {
                  setPickedOrder(event.target.value)
                  setAmount('')
                }}
                helperText="Whose balance is being let go"
              >
                {(orders?.content ?? []).map((order) => (
                  <MenuItem key={order.id} value={order.id}>
                    {order.orderNumber} — {order.customerName}
                    {order.balanceAmount > 0 ? ` · ${formatMoney(order.balanceAmount)} due` : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {shortfall ? (
              <Alert severity={shortfall.shortfall > 0 ? 'info' : 'warning'}>
                {shortfall.orderNumber} was billed {formatMoney(shortfall.totalAmount)} and{' '}
                {formatMoney(shortfall.paidAmount)} has been received
                {shortfall.alreadyWrittenOff > 0
                  ? `, with ${formatMoney(shortfall.alreadyWrittenOff)} already let go`
                  : ''}
                .{' '}
                {shortfall.shortfall > 0 ? (
                  <>
                    <strong>{formatMoney(shortfall.shortfall)}</strong> is still outstanding.{' '}
                    <Button size="small" onClick={() => setAmount(String(shortfall.shortfall))}>
                      Write off all of it
                    </Button>
                  </>
                ) : (
                  'Nothing is outstanding on it.'
                )}
              </Alert>
            ) : null}

            <TextField
              select
              fullWidth
              size="small"
              label="What this is"
              value={noteType}
              onChange={(event) => {
                setNoteType(event.target.value)
                setTax('')
              }}
              helperText={chosen?.hint}
            >
              {TYPES.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Amount"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Of which tax"
                value={taxable ? tax : ''}
                disabled={!taxable}
                onChange={(event) => setTax(event.target.value)}
                helperText={
                  taxable
                    ? `Tax on this bill ran at ${shortfall?.taxRatePercent ?? 0}%`
                    : 'No tax is reclaimed on a write-off'
                }
              />
            </Stack>

            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              size="small"
              label="Why the business is letting it go"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Settled at 22,000 by agreement; the balance is not being pursued"
            />
            <Typography variant="caption" color="text.secondary">
              This goes for approval before the balance changes.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {raised ? (
          <>
            <Button variant="contained" onClick={() => void printWriteOffVoucher(raised)}>
              Print voucher
            </Button>
            <Button onClick={() => void downloadWriteOffVoucher(raised)}>PDF</Button>
            <Button onClick={close}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!orderId || !Number(amount) || !reason.trim() || request.isPending}
              onClick={() =>
                request.mutate(
                  {
                    orderId,
                    body: {
                      noteType,
                      amount: Number(amount),
                      taxAmount: taxable && tax ? Number(tax) : 0,
                      reason: reason.trim(),
                    },
                  },
                  { onSuccess: setRaised },
                )
              }
            >
              Raise it
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
